import { Theme, styled } from "@mui/material";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import { DynamicRecaptcha } from "../../recaptcha/DynamicRecaptcha";
import EditIcon from "@mui/icons-material/Edit";
import { ErrorBox } from "../support/ErrorBox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import { FormikProps } from "formik";
import IconButton from "@mui/material/IconButton";
import Link from "next/link";
import { NewCustomerFormData } from "../new-customer-model";
import Typography from "@mui/material/Typography";
import { useSiteKey } from "../../recaptcha/useSiteKey";
import { useTranslation } from "next-i18next";

// Updated import

const termsAndConditionsUrl = "https://www.cargill.com/page/website-terms";

interface Props {
  formData: NewCustomerFormData; // Updated type
  formik: FormikProps<{ agreeToTerms: boolean; accessToken: string }>;
  handleSetStep: (step: number) => void;
}

const StyledIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  "& svg": {
    color: theme.palette.lightBlue[70],
    width: "24px",
    height: "24px",
  },
}));

export function CustomerConfirmationStep({
  formData,
  formik,
  handleSetStep,
}: Props) {
  const { t, i18n } = useTranslation();
  const sitekey = useSiteKey();

  function setRecaptchaToken(token: any) {
    if (token && token.length > 0) {
      formik.setFieldValue("accessToken", token);
    } else {
      formik.setFieldValue("accessToken", "");
    }
  }

  function clearRecaptchaToken() {
    formik.setFieldValue("accessToken", "");
  }

  const handlingEditOption = (targetIndex: number) => {
    handleSetStep(targetIndex);
  };

  return (
    <Box>
      <Box sx={{ marginBottom: "20px" }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: "1.75em",
            alignSelf: "center",
            fontWeight: "bold",
          }}
        >
          {t("request_access:customer_access.request_summary")}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: "1.25em",
            alignSelf: "center",
            fontWeight: "regular",
            marginTop: "8px", // Add spacing between title and subtitle
          }}
        >
          {t("request_access:more_information.requested_portal")} -{" "}
          {formData.cargillBusiness}
        </Typography>
      </Box>

      {/* Contact Information */}
      <Box sx={{ mt: "24px", mb: "12px", textAlign: "left" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "1em", fontWeight: "bold" }}>
            {t("request_access:customer_access.contact_information")}
          </Typography>
          <StyledIconButton
            aria-label="edit"
            onClick={() => handlingEditOption(1)}
          >
            <EditIcon />
          </StyledIconButton>
        </Box>
        <Divider />
        <Typography>
          {formData.firstName} {formData.lastName}
        </Typography>
        <Typography>{formData.role}</Typography>
        <Typography>{formData.email}</Typography>
        <Typography>{formData.phoneNumber}</Typography>
      </Box>

      {/* Business Information */}
      <Box sx={{ mt: "24px", mb: "12px", textAlign: "left" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "1em", fontWeight: "bold" }}>
            {t("request_access:customer_access.business_information")}
          </Typography>
          <StyledIconButton
            aria-label="edit"
            onClick={() => handlingEditOption(2)}
          >
            <EditIcon />
          </StyledIconButton>
        </Box>
        <Divider />
        <Typography>{formData.companyName}</Typography>
        <Typography>{formData.countryOfBusiness}</Typography>
        <Typography>{formData.address}</Typography>
        <Typography>
          {formData.city}, {formData.state}, {formData.zipCode}
        </Typography>
      </Box>

      {/* Additional Info */}
      <Box sx={{ mt: "24px", mb: "12px", textAlign: "left" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "1em", fontWeight: "bold" }}>
            {t("request_access:customer_access.additional_info")}
          </Typography>
          <StyledIconButton
            aria-label="edit"
            onClick={() => handlingEditOption(3)}
          >
            <EditIcon />
          </StyledIconButton>
        </Box>
        <Divider />
        <Typography>{formData.businessWebsite}</Typography>
        <Typography>{formData.howDidYouHear}</Typography>
        <Typography>{formData.additionalInfo}</Typography>
      </Box>

      {/* reCAPTCHA and Submit */}
      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ paddingTop: "24px" }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  id="agreeToTerms"
                  name="agreeToTerms"
                />
              }
              label={
                <>
                  {t("request_access:more_information.agree_to")}{" "}
                  <Link href={termsAndConditionsUrl} target="_blank">
                    {t("request_access:more_information.terms_and_conditions")}
                  </Link>
                </>
              }
            />
            {formik.errors.agreeToTerms && formik.touched.agreeToTerms && (
              <ErrorBox>{formik.errors.agreeToTerms}</ErrorBox>
            )}
          </FormGroup>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "32px",
            marginBottom: "16px",
          }}
        >
          <DynamicRecaptcha
            sitekey={sitekey}
            language={i18n.language}
            onChange={setRecaptchaToken}
            onExpired={clearRecaptchaToken}
          />
        </Box>

        <Box mt={12}>
          <Button
            sx={{ width: "100%", mb: 4 }}
            type="submit"
            disabled={!(formik.dirty && formik.isValid)}
          >
            {t("request_access:more_information.submit_request")}
          </Button>
        </Box>
      </form>
    </Box>
  );
}
